"use client";
import React from "react";
import PropTypes from "prop-types";

// local component imports
import VideoPlayer from "../../../../../components/atoms/videoPlayer";
import CustomImage from "../../../../../components/atoms/customImage";

/**
 * BackgroundRenderer wraps children with section background, spacing, and width settings.
 * @param {object} props - Component props.
 * @param {object} props.sectionSettings - Settings for background, margin, padding, and width.
 * @param {number|string} props.order - Render order for the section.
 * @param {React.ReactNode} props.children - Child elements to render inside the section.
 * @param {string} props.widgetType - The widget type identifier.
 * @param {string} props.gridType - The grid type identifier.
 * @returns {React.ReactElement} The section wrapper with applied background styles.
 */
const BackgroundRenderer = ({ sectionSettings, order, children, widgetType, gridType }) => {
  const background = sectionSettings?.background;
  const isContained = sectionSettings?.sectionWidth === "contained";
  const margin = sectionSettings?.margin;
  const padding = sectionSettings?.padding;

  const spacingStyles = {
    paddingTop: `${padding?.top || 0}px`,
    paddingBottom: `${padding?.bottom || 0}px`,
    marginTop: `${margin?.top || 0}px`,
    marginBottom: `${margin?.bottom || 0}px`,
    marginLeft: `${margin?.left || 0}px`,
    marginRight: `${margin?.right || 0}px`,
  };

  const containerClass = [
    isContained ? "mx-auto w-full max-w-[min(1200px,100vw)]" : "max-w-[100vw] overflow-x-hidden",
    "box-border min-w-0",
  ]
    .filter(Boolean)
    .join(" ");

  const backgroundStyle = (() => {
    if (!background?.value) {
      return background?.fallbackColor ? { backgroundColor: background.fallbackColor } : {};
    }
    switch (background.type) {
      case "gradient":
        return { background: background.value };
      case "color":
        return { backgroundColor: background.value };
      default:
        return background.fallbackColor ? { backgroundColor: "#ffffff" } : {};
    }
  })();

  const horizontalMargins = (margin?.left || 0) + (margin?.right || 0);
  const maxWidthStyle =
    horizontalMargins > 0 ? { maxWidth: `calc(100vw - ${horizontalMargins}px)` } : { maxWidth: "100vw" };

  // WCAG 2.1 SC 2.3.3 — users who set "prefers-reduced-motion: reduce" in their OS/browser
  // are sensitive to motion (vestibular disorders, epilepsy, etc.). For them we skip the
  // autoplay background video entirely and fall back to the section's fallbackColor instead.
  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      data-order={order}
      data-widget-type={widgetType}
      data-grid-type={gridType}
      data-component="BackgroundRenderer"
      aria-label={widgetType || "Page section"}
    >
      <div
        className="relative box-border w-full min-w-0 overflow-x-hidden"
        style={{ ...spacingStyles, ...backgroundStyle, ...maxWidthStyle }}
      >
        {background?.type === "video" && background.value && !prefersReducedMotion && (
          <div className="absolute inset-0" style={{ zIndex: 0 }} aria-hidden="true">
            <VideoPlayer
              videoUrl={background.value}
              isAutoPlay={true}
              isMuted={true}
              isLoop={true}
              isPlaysInline={true}
              classes="h-full w-full object-cover"
              title="Background video"
            />
          </div>
        )}
        {background?.type === "image" && background.value && (
          <div className="absolute inset-0" style={{ zIndex: 0 }} aria-hidden="true">
            <CustomImage
              src={background.value}
              alt={background.alt || "Background image"}
              width={0}
              height={0}
              fill={true}
              sizes="100vw"
              style={{ objectFit: "cover" }}
            />
          </div>
        )}
        <div className={`${containerClass} relative`} style={{ zIndex: 1 }}>
          {children}
        </div>
      </div>
    </section>
  );
};

BackgroundRenderer.propTypes = {
  sectionSettings: PropTypes.shape({
    background: PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string,
      fallbackColor: PropTypes.string,
    }),
    sectionWidth: PropTypes.string,
    margin: PropTypes.shape({
      top: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
      right: PropTypes.number,
    }),
    padding: PropTypes.shape({
      top: PropTypes.number,
      bottom: PropTypes.number,
    }),
  }),
  order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node.isRequired,
  widgetType: PropTypes.string,
  gridType: PropTypes.string,
};

export default BackgroundRenderer;
